import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Row, Col, Card, Select, Typography, Tag, Space, Button, Badge, Empty,
  Switch, Statistic,
} from 'antd';
import {
  HeatMapOutlined, ReloadOutlined, ThunderboltOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { getEvents, getHeatmap } from '../api';
import useWebSocket from '../hooks/useWebSocket';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

// Color scale: cold (low) → hot (high)
const heatColor = (intensity) => {
  if (intensity >= 80) return '#dc2626'; // Red — very hot
  if (intensity >= 60) return '#f59e0b'; // Orange — hot
  if (intensity >= 40) return '#fbbf24'; // Yellow — warm
  if (intensity >= 20) return '#22d3ee'; // Cyan — cool
  if (intensity > 0)   return '#3b82f6'; // Blue — cold
  return '#e5e7eb';                       // Grey — empty
};

const heatGradient = (intensity) => {
  const c = heatColor(intensity);
  return `radial-gradient(circle, ${c} 0%, ${c}cc 40%, ${c}33 100%)`;
};

const STYLE_ID = 'heatmap-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes hmPulse {
      0%, 100% { transform: scale(1); opacity: 0.85; }
      50%      { transform: scale(1.04); opacity: 1; }
    }
    @keyframes hmRing {
      0%   { transform: scale(0.8); opacity: 0.7; }
      100% { transform: scale(2.0); opacity: 0; }
    }
  `;
  document.head.appendChild(s);
}

export default function LiveHeatmap() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [wsActive, setWsActive] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getHeatmap({ event_id: eventId })
      .then(r => setData(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => {
    load();
    if (autoRefresh && eventId) {
      intervalRef.current = setInterval(load, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [load, autoRefresh, eventId]);

  // Real-time updates via WebSocket
  const handleWs = useCallback(() => {
    setWsActive(true);
    setTimeout(() => setWsActive(false), 2000);
    if (eventId) load();
  }, [eventId, load]);
  useWebSocket(handleWs);

  const totalNow = data.reduce((s, z) => s + (z.current_count || 0), 0);
  const hotZones = data.filter(z => z.intensity >= 60).length;
  const recentScans = data.reduce((s, z) => s + (z.recent_scans || 0), 0);

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <HeatMapOutlined style={{ marginRight: 8, color: '#dc2626' }} />Live Heat Map
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Real-time zone density visualization — auto-refreshes every 5 seconds + WebSocket pings
            {wsActive && <Tag color="green" style={{ marginLeft: 8 }}><ThunderboltOutlined /> Live update</Tag>}
          </Text>
        </Col>
        <Col>
          <Space>
            <Space size={6}><Text style={{ fontSize: 12 }}>Auto-refresh</Text>
              <Switch checked={autoRefresh} onChange={setAutoRefresh} size="small" /></Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Live Zones" value={data.length} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #10b981' }}>
            <Statistic title="Total in Zones" value={totalNow} valueStyle={{ color: '#10b981' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: '3px solid #dc2626' }}>
            <Statistic title="Hot Zones" value={hotZones} suffix={<Text type="secondary" style={{ fontSize: 12 }}>(≥60% intensity)</Text>}
              valueStyle={{ color: '#dc2626' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Scans (5 min)" value={recentScans} prefix={<ThunderboltOutlined />} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
      </Row>

      {/* Heat map grid */}
      <Card title="Zone Intensity Map" style={{ borderRadius: 10, marginBottom: 16, background: '#0f172a' }}
        headStyle={{ borderColor: '#1e293b', color: '#fff' }}
        bodyStyle={{ background: '#0f172a' }}>
        {data.length === 0 ? (
          <Empty description={<Text style={{ color: '#94a3b8' }}>No zones configured</Text>} />
        ) : (
          <Row gutter={[16, 16]}>
            {data.map(z => {
              const intensity = z.intensity || 0;
              const color = heatColor(intensity);
              const isHot = intensity >= 60;
              return (
                <Col xs={12} sm={8} md={6} lg={4} key={z.id}>
                  <div style={{
                    position: 'relative',
                    background: heatGradient(intensity),
                    borderRadius: 12,
                    padding: 16,
                    minHeight: 140,
                    border: `2px solid ${color}`,
                    boxShadow: isHot ? `0 0 28px ${color}88` : '0 0 10px rgba(0,0,0,0.3)',
                    animation: isHot ? 'hmPulse 2s ease-in-out infinite' : 'none',
                    color: '#fff',
                    overflow: 'hidden',
                  }}>
                    {/* Pulse ring for hot zones */}
                    {isHot && (
                      <div style={{
                        position: 'absolute', inset: 0, borderRadius: 12,
                        border: `2px solid ${color}`, pointerEvents: 'none',
                        animation: 'hmRing 2.4s ease-out infinite',
                      }} />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, opacity: 0.85, fontWeight: 600 }}>{z.location_type || 'Zone'}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{z.location_name}</div>
                      </div>
                      <EnvironmentOutlined style={{ fontSize: 16, opacity: 0.7 }} />
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                      {z.current_count || 0}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>
                      delegates {z.capacity > 0 && `/ ${z.capacity}`}
                    </div>
                    <div style={{
                      marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.2)',
                      display: 'flex', justifyContent: 'space-between', fontSize: 10, opacity: 0.85,
                    }}>
                      <span>1h: {z.hour_visitors || 0}</span>
                      <span>5m: {z.recent_scans || 0}</span>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Legend */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
          <Space size={20} wrap>
            <Text style={{ color: '#94a3b8', fontSize: 11 }}>Intensity scale:</Text>
            {[
              { l: 'Empty',  c: '#e5e7eb' },
              { l: 'Cold',   c: '#3b82f6' },
              { l: 'Cool',   c: '#22d3ee' },
              { l: 'Warm',   c: '#fbbf24' },
              { l: 'Hot',    c: '#f59e0b' },
              { l: 'On Fire', c: '#dc2626' },
            ].map(s => (
              <Space key={s.l} size={6}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: s.c, display: 'inline-block' }}/>
                <Text style={{ color: '#cbd5e1', fontSize: 11 }}>{s.l}</Text>
              </Space>
            ))}
          </Space>
        </div>
      </Card>
    </div>
  );
}
